import React from "react";
import { bool, func, node, oneOf, oneOfType, string } from "prop-types";
import cx from "classnames";

import Label from "../Label";
import Message from "../Message";

import Addons from "./Addons";

const CLASS_ROOT = "input";

export default class Input extends React.Component {
  static displayName = "Input";

  static propTypes = {
    /** Error state. If vaule is string, it's treated as error message. */
    error: oneOfType([bool, node]),
    /** Help text. */
    help: string,
    /** Html id attribute. */
    id: string.isRequired,
    /** Active state. */
    isActive: bool,
    /** Disabled state. */
    isDisabled: bool,
    /** Readonly state. */
    isReadonly: bool,
    /** Label text. */
    label: string,
    leftAddons: Addons.propTypes.left,
    /** Multiline input */
    multiline: bool,
    /** Html name attribute. */
    name: string,
    /** Value change callback function. */
    onChange: func,
    /** Placeholder text */
    placeholder: string,
    /** Custom error renderer. Passes props as function parameter. */
    renderError: func,
    /** Custom label renderer. Passes props as function parameter. */
    /** Custom help renderer. Passes props as function parameter. */
    renderHelp: func,
    renderLabel: func,
    rightAddons: Addons.propTypes.right,
    /** Size of element. */
    size: oneOf(["small", "large"]),
    /** Input html type. */
    type: string,
    /** Input value. */
    value: string,
  };

  static defaultProps = {
    onChange: (newValue, e) => {}, // eslint-disable-line
    type: "text",
  };

  // eslint-disable-next-line react/state-in-constructor
  state = {
    // eslint-disable-next-line react/destructuring-assignment
    value: this.props.value,
  };

  // eslint-disable-next-line react/no-deprecated
  componentWillReceiveProps(nextProps) {
    this.setState({ value: nextProps.value });
  }

  handleChange = (e) => {
    const { onChange } = this.props;

    onChange(e.target.value, e);
    this.setState({ value: e.target.value });
  };

  render() {
    const {
      children,
      className,
      error,
      help,
      id,
      isActive,
      isDisabled,
      isReadonly,
      label,
      leftAddons,
      multiline,
      placeholder,
      renderError,
      renderHelp,
      renderLabel,
      rightAddons,
      size,
      type,
      ...other
    } = this.props;

    const { value } = this.state;

    const classes = cx(
      CLASS_ROOT,
      {
        "is-active": isActive,
        "is-error": error,
        [`${CLASS_ROOT}--${size}`]: size,
      },
      className
    );

    const formControlClasses = cx(
      "form-control",
      `form-control--${multiline ? "textarea" : "input"}`
    );

    // set name from id if name is not specfied
    let { name } = other;
    name = name && id;

    const Element = multiline ? "textarea" : "input";

    const inputLabel =
      (renderLabel && renderLabel(this.props)) ||
      (label && (
        <Label id={id} size={size} isError={!!error}>
          {label}
        </Label>
      ));

    const helpMessage =
      (renderHelp && renderHelp(this.props)) ||
      (help && <Message type="help">{help}</Message>);

    const errorMessage =
      (renderError && renderError(this.props)) ||
      (error && typeof error === "string" && (
        <Message type="error">{error}</Message>
      ));

    const inputElement = (
      <Element
        ref={(c) => {
          this.inputRef = c;
        }}
        className={classes}
        id={id}
        name={name}
        value={value}
        type={type}
        disabled={isDisabled}
        readOnly={isReadonly}
        placeholder={placeholder}
        onChange={this.handleChange}
        {...other}
      />
    );

    return (
      <div className={formControlClasses}>
        {inputLabel}

        {leftAddons || rightAddons ? (
          <Addons left={leftAddons} right={rightAddons} size={size}>
            {inputElement}
          </Addons>
        ) : (
          inputElement
        )}
        {helpMessage}
        {errorMessage}
      </div>
    );
  }
}
